import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const { REACT_APP_BACKEND_URL } = process.env;

const Register = () => {
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    showPassword: false,
    company: "",
  });

  const [tenants, setTenants] = useState([]);

  const {
    firstname,
    lastname,
    email,
    password,
    confirmPassword,
    showPassword,
    company,
  } = formData;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await axios.get(`${REACT_APP_BACKEND_URL}/api/tenants/all-tenants`);
        setTenants(res.data);
      } catch (err) {
        console.error(err.response.data);
        toast.error("Failed to load tenants 🚫🚫");
      }
    };

    fetchTenants();
  }, []);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const togglePasswordVisibility = () => {
    setFormData({ ...formData, showPassword: !showPassword });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match!! 🚫🚫");
      return;
    }
    try {
      const res = await axios.post(
        `${REACT_APP_BACKEND_URL}/api/auth/register`,
        { firstname, lastname, email, password, company }
      );
      toast.success(res.data.msg);
      navigate("/dashboard");
    } catch (err) {
      console.error(err.response.data);
      toast.error(err.response.data.msg || "Registration failed 🚫🚫");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg shadow-2xl rounded-2xl mt-16 p-8 w-full max-w-3xl"
      >
        <h1 className="text-3xl font-bold mb-8 text-center text-white">
          Create An Account
        </h1>
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="First Name"
                name="firstname"
                value={firstname}
                onChange={onChange}
                required
                className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              />
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Last Name"
                name="lastname"
                value={lastname}
                onChange={onChange}
                required
                className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              />
            </div>
          </div>
          <div className="relative">
            <input
              type="email"
              placeholder="Email"
              name="email"
              value={email}
              onChange={onChange}
              required
              className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                name="password"
                value={password}
                minLength="8"
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible className="text-gray-400 h-5 w-5" />
                ) : (
                  <AiOutlineEye className="text-gray-400 h-5 w-5" />
                )}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Confirm Password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={onChange}
                className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              />
            </div>
          </div>
          <div className="relative">
            <select
              name="company"
              value={company}
              onChange={onChange}
              required
              className="w-full px-4 py-3 bg-gray-800 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 appearance-none"
            >
              <option value="" disabled>
                Select Company
              </option>
              {tenants.map((tenant) => (
                <option key={tenant._id} value={tenant._id}>
                  {tenant.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(59, 130, 246, 0.5)" }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold text-lg shadow-lg hover:from-blue-600 hover:to-indigo-700 transition duration-300"
          >
            Register
          </motion.button>
        </form>
        <p className="mt-6 text-center text-gray-300">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-400 font-semibold hover:text-blue-300 transition duration-300">
            Login
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Register;